/**
 * Cache storage layer using IndexedDB via @push.rocks/webstore
 */

import * as plugins from '../webrequest.plugins.js';
import type { ICacheEntry } from '../webrequest.types.js';

export const defaultCacheMaxEntries = 256;

export interface ICacheStoreOperationOptions {
  signal?: AbortSignal;
  /**
   * Signal already governing the source response. When it aborts, the Fetch
   * implementation owns source-body cancellation.
   */
  responseSignal?: AbortSignal;
  /**
   * Preserve the source response for a caller by caching a clone. Background
   * consumers can disable this to consume the response without creating a
   * second tee branch.
   */
  preserveResponse?: boolean;
}

export interface ICacheStoreWriteOptions extends ICacheStoreOperationOptions {
  maxEntries?: number;
}

interface ICacheEntryDescriptor {
  key: string;
  valid: boolean;
  timestamp: number;
  maxAge?: number;
}

interface IPruneWaiter {
  generation: number;
  resolve: () => void;
  reject: (errorArg: unknown) => void;
}

export class CacheStore {
  private webstore: plugins.webstore.WebStore<ICacheEntry>;
  private initPromise: Promise<void>;
  private lifecycleController = new AbortController();
  private readonly closeReason = new Error('Webrequest cache store is closed');
  private disposed = false;
  private closePromise?: Promise<void>;
  private pruneLoopPromise?: Promise<void>;
  private pendingPruneMaxEntries?: number;
  private pruneGeneration = 0;
  private pruneWaiters = new Set<IPruneWaiter>();
  private activeOwnedOperations = new Set<Promise<unknown>>();

  constructor(dbName: string = 'webrequest-v4', storeName: string = 'cache') {
    this.webstore = new plugins.webstore.WebStore<ICacheEntry>({
      dbName,
      storeName,
    });

    // Initialize the store
    this.initPromise = this.init();
  }

  /**
   * Initialize the store
   */
  private async init(): Promise<void> {
    // WebStore handles initialization internally
    // This method exists for future extension if needed
  }

  /**
   * Generate a cache key from a request
   */
  public generateCacheKey(request: Request): string {
    // Use URL + method as the base key
    const url = request.url;
    const method = request.method;

    // For GET requests, just use the URL
    if (method === 'GET') {
      return url;
    }

    // For other methods, include the method
    return `${method}:${url}`;
  }

  /**
   * Store a response in the cache
   */
  public async set(
    cacheKey: string,
    entry: ICacheEntry,
    options: ICacheStoreWriteOptions = {},
  ): Promise<void> {
    await this.initPromise;
    const signal = this.getOperationSignal(options.signal);
    const maxEntries = options.maxEntries ?? defaultCacheMaxEntries;
    if (!Number.isSafeInteger(maxEntries) || maxEntries <= 0) {
      throw new RangeError(
        'webrequest cache maxEntries must be a positive safe integer',
      );
    }
    await this.webstore.set(cacheKey, entry, {
      signal,
    });
    await this.schedulePrune(maxEntries, signal);
  }

  /**
   * Retrieve a cached response
   */
  public async get(
    cacheKey: string,
    options: ICacheStoreOperationOptions = {},
  ): Promise<ICacheEntry | null> {
    await this.initPromise;
    const signal = this.getOperationSignal(options.signal);

    try {
      const entry = await this.webstore.get(cacheKey, {
        signal,
      });
      if (entry === undefined) {
        return null;
      }
      if (!this.isCacheEntry(entry)) {
        await this.webstore.delete(cacheKey, {
          signal,
        });
        return null;
      }
      return entry;
    } catch (error) {
      if (signal.aborted) {
        throw signal.reason;
      }
      // If an entry doesn't exist or IndexedDB rejected malformed data, return null.
      return null;
    }
  }

  /**
   * Check if a cache entry exists
   */
  public async has(
    cacheKey: string,
    options: ICacheStoreOperationOptions = {},
  ): Promise<boolean> {
    return (await this.get(cacheKey, options)) !== null;
  }

  /**
   * Delete a cache entry
   */
  public async delete(
    cacheKey: string,
    options: ICacheStoreOperationOptions = {},
  ): Promise<void> {
    await this.initPromise;
    const signal = this.getOperationSignal(options.signal);
    await this.webstore.delete(cacheKey, {
      signal,
    });
  }

  /**
   * Clear all cache entries
   */
  public async clear(
    options: ICacheStoreOperationOptions = {},
  ): Promise<void> {
    await this.initPromise;
    const signal = this.getOperationSignal(options.signal);
    await this.webstore.clear({
      signal,
    });
  }

  /**
   * Create a Response object from a cache entry
   */
  public responseFromCacheEntry(entry: ICacheEntry): Response {
    const headers = new Headers(entry.headers);

    return new Response(entry.response, {
      status: entry.status,
      statusText: entry.statusText,
      headers,
    });
  }

  /**
   * Create a cache entry from a Response object
   */
  public cacheEntryFromResponse(
    url: string,
    response: Response,
    metadata?: { maxAge?: number; etag?: string; lastModified?: string },
    options: ICacheStoreOperationOptions = {},
  ): Promise<ICacheEntry> {
    return this.trackOwnedOperation(async () => {
      const signal = this.getOperationSignal(options.signal);
      const preserveResponse = options.preserveResponse !== false;
      const cacheResponse = preserveResponse
        ? response.clone()
        : response;
      const buffer = await this.consumeArrayBuffer(
        cacheResponse,
        signal,
        preserveResponse ? response.body : undefined,
        options.responseSignal,
      );

      // Extract headers
      const headers: Record<string, string> = {};
      cacheResponse.headers.forEach((value, key) => {
        headers[key] = value;
      });

      return {
        response: buffer,
        headers,
        timestamp: Date.now(),
        etag:
          metadata?.etag ||
          cacheResponse.headers.get('etag') ||
          undefined,
        lastModified:
          metadata?.lastModified ||
          cacheResponse.headers.get('last-modified') ||
          undefined,
        maxAge: metadata?.maxAge,
        url,
        status: cacheResponse.status,
        statusText: cacheResponse.statusText,
      };
    });
  }

  /**
   * Prune expired entries (garbage collection)
   * Returns the number of entries deleted
   */
  public async pruneExpired(
    options: ICacheStoreOperationOptions = {},
  ): Promise<number> {
    await this.initPromise;
    const signal = this.getOperationSignal(options.signal);
    const entries = await this.collectEntries(signal);
    let deletedCount = 0;
    for (const entry of entries) {
      signal.throwIfAborted();
      if (entry.valid && !this.isExpired(entry)) {
        continue;
      }
      await this.webstore.delete(entry.key, {
        signal,
      });
      deletedCount += 1;
    }
    return deletedCount;
  }

  private async schedulePrune(
    maxEntriesArg: number,
    signalArg?: AbortSignal,
  ): Promise<void> {
    signalArg?.throwIfAborted();
    const generation = ++this.pruneGeneration;
    this.pendingPruneMaxEntries =
      this.pendingPruneMaxEntries === undefined
        ? maxEntriesArg
        : Math.min(this.pendingPruneMaxEntries, maxEntriesArg);
    let resolveWaiter!: () => void;
    let rejectWaiter!: (errorArg: unknown) => void;
    const waiterPromise = new Promise<void>((resolveArg, rejectArg) => {
      resolveWaiter = resolveArg;
      rejectWaiter = rejectArg;
    });
    const waiter: IPruneWaiter = {
      generation,
      resolve: resolveWaiter,
      reject: rejectWaiter,
    };
    this.pruneWaiters.add(waiter);
    this.ensurePruneLoop();
    try {
      await awaitWithSignal(waiterPromise, signalArg);
    } finally {
      this.pruneWaiters.delete(waiter);
    }
  }

  private ensurePruneLoop(): Promise<void> {
    if (this.pruneLoopPromise) {
      return this.pruneLoopPromise;
    }
    let trackedPruneLoop!: Promise<void>;
    trackedPruneLoop = this.runPruneLoop()
      .catch((errorArg: unknown) => {
        this.pendingPruneMaxEntries = undefined;
        this.settlePruneWaiters(this.pruneGeneration, errorArg);
      })
      .finally(() => {
        if (this.pruneLoopPromise === trackedPruneLoop) {
          this.pruneLoopPromise = undefined;
          if (
            this.pendingPruneMaxEntries !== undefined &&
            !this.disposed
          ) {
            this.ensurePruneLoop();
          }
        }
      });
    this.pruneLoopPromise = trackedPruneLoop;
    return trackedPruneLoop;
  }

  private async runPruneLoop(): Promise<void> {
    while (this.pendingPruneMaxEntries !== undefined) {
      const maxEntries = this.pendingPruneMaxEntries;
      this.pendingPruneMaxEntries = undefined;
      const passGeneration = this.pruneGeneration;
      await this.pruneToLimit(
        maxEntries,
        this.lifecycleController.signal,
      );
      this.settlePruneWaiters(passGeneration);
    }
  }

  private settlePruneWaiters(
    throughGenerationArg: number,
    errorArg?: unknown,
  ): void {
    for (const waiter of this.pruneWaiters) {
      if (waiter.generation > throughGenerationArg) {
        continue;
      }
      this.pruneWaiters.delete(waiter);
      if (errorArg === undefined) {
        waiter.resolve();
      } else {
        waiter.reject(errorArg);
      }
    }
  }

  private async pruneToLimit(
    maxEntriesArg: number,
    signalArg?: AbortSignal,
  ): Promise<void> {
    const entries = await this.collectEntries(signalArg);
    const malformedOrExpired = entries.filter(
      (entryArg) => !entryArg.valid || this.isExpired(entryArg),
    );
    for (const entry of malformedOrExpired) {
      signalArg?.throwIfAborted();
      await this.webstore.delete(entry.key, { signal: signalArg });
    }

    const validEntries = entries
      .filter(
        (entryArg) => entryArg.valid && !this.isExpired(entryArg),
      )
      .sort((entryA, entryB) =>
        entryA.timestamp === entryB.timestamp
          ? entryA.key.localeCompare(entryB.key)
          : (entryA.timestamp ?? 0) - (entryB.timestamp ?? 0),
      );
    const excessCount = Math.max(0, validEntries.length - maxEntriesArg);
    for (const entry of validEntries.slice(0, excessCount)) {
      signalArg?.throwIfAborted();
      await this.webstore.delete(entry.key, { signal: signalArg });
    }
  }

  private async collectEntries(
    signalArg?: AbortSignal,
  ): Promise<ICacheEntryDescriptor[]> {
    signalArg?.throwIfAborted();
    const keys = await this.webstore.keys({ signal: signalArg });
    const entries: ICacheEntryDescriptor[] = [];
    for (const key of keys) {
      signalArg?.throwIfAborted();
      if (typeof key !== 'string') {
        continue;
      }
      try {
        const candidate = await this.webstore.get(key, {
          signal: signalArg,
        });
        entries.push(
          this.isCacheEntry(candidate)
            ? {
                key,
                valid: true,
                timestamp: candidate.timestamp,
                maxAge: candidate.maxAge,
              }
            : { key, valid: false, timestamp: 0 },
        );
      } catch (error) {
        if (signalArg?.aborted) {
          throw signalArg.reason;
        }
        continue;
      }
    }
    return entries;
  }

  private isCacheEntry(entryArg: unknown): entryArg is ICacheEntry {
    if (!entryArg || typeof entryArg !== 'object') {
      return false;
    }
    const response = Reflect.get(entryArg, 'response');
    const headers = Reflect.get(entryArg, 'headers');
    const timestamp = Reflect.get(entryArg, 'timestamp');
    const url = Reflect.get(entryArg, 'url');
    const status = Reflect.get(entryArg, 'status');
    const statusText = Reflect.get(entryArg, 'statusText');
    return (
      response instanceof ArrayBuffer &&
      typeof headers === 'object' &&
      headers !== null &&
      Number.isFinite(timestamp) &&
      typeof url === 'string' &&
      Number.isInteger(status) &&
      typeof statusText === 'string'
    );
  }

  private isExpired(
    entryArg: Pick<ICacheEntry, 'timestamp' | 'maxAge'>,
  ): boolean {
    return (
      entryArg.maxAge !== undefined &&
      entryArg.maxAge > 0 &&
      Date.now() - entryArg.timestamp >= entryArg.maxAge
    );
  }

  private getOperationSignal(signalArg?: AbortSignal): AbortSignal {
    if (this.disposed) {
      throw this.closeReason;
    }
    signalArg?.throwIfAborted();
    return signalArg
      ? AbortSignal.any([signalArg, this.lifecycleController.signal])
      : this.lifecycleController.signal;
  }

  private trackOwnedOperation<TResult>(
    operationArg: () => Promise<TResult>,
  ): Promise<TResult> {
    let trackedOperation!: Promise<TResult>;
    trackedOperation = operationArg().finally(() => {
      this.activeOwnedOperations.delete(trackedOperation);
    });
    this.activeOwnedOperations.add(trackedOperation);
    return trackedOperation;
  }

  /**
   * Abort pending cache work, wait for owned operations, and close IndexedDB.
   * A closed CacheStore cannot be reopened.
   */
  public close(): Promise<void> {
    if (this.closePromise) {
      return this.closePromise;
    }
    this.disposed = true;
    this.lifecycleController.abort(this.closeReason);
    const pendingPrune = this.pruneLoopPromise;
    const ownedOperations = [...this.activeOwnedOperations];
    this.closePromise = (async () => {
      await Promise.allSettled([
        ...ownedOperations,
        ...(pendingPrune ? [pendingPrune] : []),
      ]);
      await this.webstore.close();
    })();
    return this.closePromise;
  }

  private async consumeArrayBuffer(
    responseArg: Response,
    signalArg?: AbortSignal,
    siblingBodyArg?: ReadableStream<Uint8Array> | null,
    responseSignalArg?: AbortSignal,
  ): Promise<ArrayBuffer> {
    signalArg?.throwIfAborted();
    if (!signalArg) {
      return responseArg.arrayBuffer();
    }
    const reader = responseArg.body?.getReader();
    if (!reader) {
      return new ArrayBuffer(0);
    }

    const chunks: Uint8Array[] = [];
    let totalLength = 0;
    let rejectForAbort!: (reason?: unknown) => void;
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectForAbort = reject;
    });
    let cancellationPromise: Promise<void> | undefined;
    const abortListener = () => {
      rejectForAbort(signalArg.reason);
      if (!responseSignalArg?.aborted) {
        cancellationPromise = Promise.all([
          reader.cancel(signalArg.reason).catch(() => undefined),
          siblingBodyArg?.cancel(signalArg.reason).catch(() => undefined),
        ]).then(() => undefined);
      }
    };
    signalArg.addEventListener('abort', abortListener, { once: true });
    try {
      while (true) {
        const readResult = await Promise.race([
          reader.read(),
          abortPromise,
        ]);
        if (readResult.done) {
          break;
        }
        chunks.push(readResult.value);
        totalLength += readResult.value.byteLength;
      }
    } finally {
      signalArg.removeEventListener('abort', abortListener);
      if (cancellationPromise) {
        await cancellationPromise;
      }
      try {
        reader.releaseLock();
      } catch {}
    }

    const buffer = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      buffer.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return buffer.buffer;
  }
}

const awaitWithSignal = async <TResult>(
  promiseArg: Promise<TResult>,
  signalArg?: AbortSignal,
): Promise<TResult> => {
  signalArg?.throwIfAborted();
  if (!signalArg) {
    return promiseArg;
  }

  let rejectForAbort!: (reason?: unknown) => void;
  const abortPromise = new Promise<never>((_resolve, reject) => {
    rejectForAbort = reject;
  });
  const abortListener = () => {
    rejectForAbort(signalArg.reason);
  };
  signalArg.addEventListener('abort', abortListener, { once: true });
  try {
    return await Promise.race([promiseArg, abortPromise]);
  } finally {
    signalArg.removeEventListener('abort', abortListener);
  }
};
